import * as React from "react"
import {
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  Button,
  StyledModalHeader,
  useShowSuccessToast,
} from "gatsby-interface"
import { Text } from "./shared/Text"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.js"

import {
  HostingDetailsForSiteDocument,
  useRequestCloudCdnCertificateMutation,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { interpolateMessage } from "@modules/locales"

export type RequestCertificateFormProps = {
  siteId: string
  domain: string
  onDismiss: () => void
  onComplete: () => void
}

export function RequestCertificateForm({
  siteId,
  domain,
  onDismiss,
  onComplete,
}: RequestCertificateFormProps) {
  const [
    requestCloudCdnCertificateMutation,
  ] = useRequestCloudCdnCertificateMutation()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()
  const [loading, setLoading] = React.useState(false)

  const handleManagedCertificate = async () => {
    setLoading(true)

    requestCloudCdnCertificateMutation({
      variables: {
        siteId,
        domainName: domain,
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
      ],
    })
      .then(({ data }) => {
        setLoading(false)
        const success = data?.requestCloudCdnCertificate?.success ?? false

        if (success) {
          onComplete()
          return showSuccessToast(
            <FormattedMessage<"domain", "strong">
              message={text.messages.managedCertificateTurnedOn}
              values={{ domain }}
              tags={commonMessageTags}
            />
          )
        }

        const message =
          data?.requestCloudCdnCertificate?.message ||
          errorText.messages.somethingWentWrong
        setError({ message })
      })
      .catch(e => {
        setLoading(false)
        setError(e)
      })
  }

  return (
    <Modal
      isOpen={Boolean(domain)}
      aria-label={interpolateMessage<"domain">(
        text.messages.doYouWantToTurnOnAutomaticCertificateAria,
        { domain: domain }
      )}
      onDismiss={onDismiss}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="ACTION">
          <StyledModalHeader onCloseButtonClick={onDismiss}>
            {text.headers.turnOnAutomaticCertificate}
          </StyledModalHeader>

          <StyledModalBody>
            <Text>
              <FormattedMessage<"domain", "strong">
                message={text.messages.doYouWantToTurnOnAutomaticCertificate}
                values={{ domain }}
                tags={commonMessageTags}
              />
            </Text>

            {errorAlert && (
              <div
                css={theme => ({
                  marginTop: theme.space[5],
                })}
              >
                {errorAlert}
              </div>
            )}

            <StyledModalActions>
              <Button type="button" onClick={onDismiss} variant="SECONDARY">
                {uiText.actions.cancel}
              </Button>
              <Button
                type="button"
                onClick={handleManagedCertificate}
                variant="PRIMARY"
                loading={loading}
                loadingLabel={uiText.labels.updating}
              >
                {uiText.actions.turnOn}
              </Button>
            </StyledModalActions>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
